import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  Stack,
  InputAdornment,
  Autocomplete,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Search, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import BasicButton from '../../../components/button/BasicButton';
import ActionDialog from '../../../components/dialog/ActionDialog';
import { adminApi } from '../../../api/admin';
import ProgressBar from '../../../components/common/ProgressBar';
import ErrorState from '../../../components/common/ErrorStats';
import NoData from '../../../components/common/NoData';
import Pagination from '../../../components/common/Pagination';
import theme from '../../../theme';

const ROWS_PER_PAGE = 20;

const HEAD_CELLS = [
  { id: 'ARTIST_NAME', label: '아티스트', sortable: true },
  { id: 'ALBUM_TITLE', label: '앨범', sortable: true },
  { id: 'RELEASE_DATE', label: '발매일', sortable: true },
  { id: 'VIEW_COUNT', label: '조회수', sortable: true },
  { id: 'REVIEW_COUNT', label: '리뷰수', sortable: true },
  { id: 'BOOKMARK_COUNT', label: '저장수', sortable: true },
];

const ActiveReviewManagement = ({ searchParams, updateURL, onCountsChange }) => {
  const debouncedSearchTerm = searchParams.get('search') || '';
  const sortField = searchParams.get('sortField') || 'ALBUM_TITLE';
  const sortOrder = searchParams.get('sortOrder') || 'asc';
  const currentPage = parseInt(searchParams.get('page') || '1');

  const [searchTerm, setSearchTerm] = useState(debouncedSearchTerm);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({ totalPages: 1, totalItems: 0 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState([]);
  const [isStarDialogOpen, setIsStarDialogOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchAlbums = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getActiveAlbums(
          currentPage,
          ROWS_PER_PAGE,
          debouncedSearchTerm,
          sortField,
          sortOrder,
        );
        setAlbums(response.data);
        setPaginationInfo(response.meta.pagination);
      } catch (err) {
        setError(err);
        setAlbums([]);
        setPaginationInfo({ totalPages: 1, totalItems: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, [currentPage, debouncedSearchTerm, refetchTrigger, sortField, sortOrder]);

  const handleSearchClick = () => {
    updateURL({ search: searchTerm || null, page: null });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSort = (field) => {
    const isCurrentField = sortField === field;
    const newSortOrder = isCurrentField && sortOrder === 'desc' ? 'asc' : 'desc';
    updateURL({ sortField: field, sortOrder: newSortOrder, page: null });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const currentPageAlbumIds = albums.map((album) => album.albumId);
      setSelectedAlbumIds((prev) => [...new Set([...prev, ...currentPageAlbumIds])]);
      return;
    }
    const currentPageAlbumIds = albums.map((album) => album.albumId);
    setSelectedAlbumIds((prev) => prev.filter((id) => !currentPageAlbumIds.includes(id)));
  };

  const handleSelectClick = (event, id) => {
    const selectedIndex = selectedAlbumIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedAlbumIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedAlbumIds.slice(1));
    } else if (selectedIndex === selectedAlbumIds.length - 1) {
      newSelected = newSelected.concat(selectedAlbumIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedAlbumIds.slice(0, selectedIndex),
        selectedAlbumIds.slice(selectedIndex + 1),
      );
    }
    setSelectedAlbumIds(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    updateURL({ page: newPage });
  };

  const handleStar = async () => {
    try {
      await adminApi.starAlbums(selectedAlbumIds);
      setIsStarDialogOpen(false);
      setSelectedAlbumIds([]);
      setRefetchTrigger((c) => c + 1);
      onCountsChange?.();
    } catch (err) {
      // 에러 처리
    }
  };

  const numSelectedOnPage = useMemo(
    () => albums.filter((album) => selectedAlbumIds.includes(album.albumId)).length,
    [albums, selectedAlbumIds],
  );

  const isAllOnPageSelected = albums.length > 0 && numSelectedOnPage === albums.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllOnPageSelected;
  const hasSelection = selectedAlbumIds.length > 0;

  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };
  const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };

  return (
    <>
      {/* 검색 */}
      <Stack spacing={'8px'} sx={{ mb: '30px' }}>
        <Typography fontSize={14} color={theme.palette.black[7]}>
          앨범 검색
        </Typography>
        <Box
          sx={{
            p: 1.5,
            width: '100%',
            border: `1px solid ${theme.palette.grey.line}`,
            borderRadius: 0.5,
          }}
        >
          <Autocomplete
            options={[]}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="아티스트 또는 앨범 검색"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search
                        sx={{
                          width: { xs: 20, lg: 16 },
                          height: { xs: 20, lg: 16 },
                          color: theme.palette.black[1],
                        }}
                      />
                    </InputAdornment>
                  ),
                  onKeyPress: handleSearchKeyDown,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '16px', lg: '14px' },
                    padding: '0 !important',
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '0 !important',
                  },
                }}
              />
            )}
            value={searchTerm}
            inputValue={searchTerm}
            onSubmit={handleSearchClick}
            onInputChange={(_, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
          />
        </Box>
      </Stack>

      {/* 액션 버튼 */}
      <BasicButton
        variant="outlined"
        onClick={() => setIsStarDialogOpen(true)}
        disabled={!hasSelection}
        sx={{ ...buttonStyle, mb: { xs: '20px', md: '16px' } }}
      >
        P 마크 추가
      </BasicButton>

      {/* 앨범 목록 테이블 */}
      <ProgressBar isLoading={loading}>
        {error ? (
          <ErrorState />
        ) : !loading && albums.length === 0 ? (
          <NoData message="결과 없음" />
        ) : (
          <Stack spacing={7.5}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                    <TableCell padding="checkbox" sx={cellStyle}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllOnPageSelected}
                        onChange={handleSelectAllClick}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                          '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    {HEAD_CELLS.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        align="center"
                        sortDirection={sortField === headCell.id ? sortOrder : false}
                        sx={{
                          whiteSpace: 'nowrap',
                          borderRight:
                            index === HEAD_CELLS.length - 1
                              ? 'none'
                              : `1px solid ${theme.palette.grey.line}`,
                        }}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={true}
                            direction={sortField === headCell.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: theme.palette.black[3],
                              },
                            }}
                          >
                            <Typography fontSize={14} color={theme.palette.black[4]}>
                              {headCell.label}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography fontSize={14} color={theme.palette.black[4]}>
                            {headCell.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {albums.map((album) => {
                    const isItemSelected = selectedAlbumIds.indexOf(album.albumId) !== -1;
                    return (
                      <TableRow
                        key={album.albumId}
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox" sx={cellStyle}>
                          <Checkbox
                            checked={isItemSelected}
                            onClick={(event) => handleSelectClick(event, album.albumId)}
                            size="small"
                            sx={{
                              color: theme.palette.grey.line,
                              '&.Mui-checked': { color: '#f16438' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 300 }}>
                          <Link
                            component={RouterLink}
                            to={
                              album.artistSlug
                                ? `/artists/${album.artistSlug}`
                                : `/artists/${album.artistSource?.toLowerCase()}/${album.artistSourceId}`
                            }
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography {...textStyle}>{album.artistName}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 392 }}>
                          <Link
                            component={RouterLink}
                            to={
                              album.albumSlug
                                ? `/reviews/${album.albumSlug}`
                                : `/reviews/local/${album.albumId}`
                            }
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography {...textStyle}>{album.albumTitle}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>{album.releaseDate || '-'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>{album.viewCount.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>
                            {album.reviewCount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography {...textStyle}>
                            {album.bookmarkCount.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={paginationInfo.totalPages}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Stack>
        )}
      </ProgressBar>

      {/* P 마크 추가 확인 다이얼로그 */}
      <ActionDialog
        open={isStarDialogOpen}
        onClose={() => setIsStarDialogOpen(false)}
        onConfirm={handleStar}
        confirmText="확인"
      >
        <Typography sx={{ textAlign: 'center' }}>P 마크를 추가하시겠습니까?</Typography>
      </ActionDialog>
    </>
  );
};

export default ActiveReviewManagement;
